import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { useDemoWorkspaceStore } from '@/store/demo-workspace';
import { DemoWorkspace, WorkspaceType } from '@/store/demo-workspace/types';

interface CreateWorkspaceDialogProps {
  trigger?: React.ReactNode;
}

export const CreateWorkspaceDialog = ({ trigger }: CreateWorkspaceDialogProps) => {
  const [open, setOpen] = useState(false);
  const addWorkspace = useDemoWorkspaceStore((state) => state.addWorkspace);
  const workspaces = useDemoWorkspaceStore((state) => state.workspaces);

  const [name, setName] = useState('');
  const [type, setType] = useState<WorkspaceType>('workspace');
  const [quotas, setQuotas] = useState({
    h200: 0,
    a100: 0,
    l4: 0,
  });

  const handleQuotaChange = (gpuType: 'h200' | 'a100' | 'l4', value: string) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    setQuotas((prev) => ({ ...prev, [gpuType]: numValue }));
  };

  const handleSubmit = () => {
    if (!name.trim()) return;

    // Generate unique ID
    const id = name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();

    // Check if workspace already exists
    if (workspaces.some((w) => w.name.toLowerCase() === name.toLowerCase())) {
      return;
    }

    const newWorkspace: DemoWorkspace = {
      id,
      name: name.trim(),
      type,
      role: 'Owner',
      organizationId: 'float16-inc',
      quota: {
        h200: { total: quotas.h200, used: 0 },
        a100: { total: quotas.a100, used: 0 },
        l4: { total: quotas.l4, used: 0 },
      },
      instances: [],
      members: [
        {
          id: 'member-owner',
          email: 'demo@float16.cloud',
          name: 'Demo User',
          role: 'Owner',
          permissions: { read: true, create: true, delete: true },
          joinedAt: new Date().toISOString(),
        },
      ],
      createdAt: new Date().toISOString(),
    };

    addWorkspace(newWorkspace);
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setType('workspace');
    setQuotas({ h200: 0, a100: 0, l4: 0 });
    setOpen(false);
  };

  const isValid = name.trim() !== '';

  const defaultTrigger = (
    <Button>
      <Plus className="h-4 w-4 mr-2" />
      Create Workspace
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Workspace</DialogTitle>
          <DialogDescription>
            Create a new workspace to organize your GPU instances
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              placeholder="My Workspace"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="workspace-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as WorkspaceType)}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="workspace">Workspace</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label>Initial GPU Quotas</Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="quota-h200" className="text-xs text-muted-foreground">
                  H200
                </Label>
                <Input
                  id="quota-h200"
                  type="number"
                  min="0"
                  value={quotas.h200}
                  onChange={(e) => handleQuotaChange('h200', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="quota-a100" className="text-xs text-muted-foreground">
                  A100
                </Label>
                <Input
                  id="quota-a100"
                  type="number"
                  min="0"
                  value={quotas.a100}
                  onChange={(e) => handleQuotaChange('a100', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="quota-l4" className="text-xs text-muted-foreground">
                  L4
                </Label>
                <Input
                  id="quota-l4"
                  type="number"
                  min="0"
                  value={quotas.l4}
                  onChange={(e) => handleQuotaChange('l4', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
